import { AccessTimeOutlined, ArrowDropDown, ArrowDropUp, PeopleOutline } from "@mui/icons-material";
import { Alert, Box, Button, Chip, CircularProgress, Link, Paper, Typography } from "@mui/material";
import React from "react";
import { UnifiedInsightsUsageMetricsResponse } from "../../../interfaces/CloseTheLoop.interfaces";

type UsageMetricsCardsProps = {
  data: UnifiedInsightsUsageMetricsResponse[];
  product: string;
  timeframe: string;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  children?: React.ReactNode;
};

const metrics = [
  {
    label: "Active Customers",
    title: "Weekly Active Customers",
    icon: PeopleOutline,
    color: "#0F6CBD",
    lowerIsBetter: false
  },
  {
    label: "Time to Onboard",
    title: "Time to Onboard",
    icon: AccessTimeOutlined,
    color: "#8764B8",
    lowerIsBetter: true
  }
];
const valueFormat = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
const changeFormat = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });
const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

const parseUsageHistory = (
  dataStatus: UnifiedInsightsUsageMetricsResponse["dataStatus"]
): { previousWeekValue: number | null; fourWeeksAgoValue: number | null; error?: string } => {
  const unavailable = { previousWeekValue: null, fourWeeksAgoValue: null };
  if (dataStatus === null || dataStatus === undefined || dataStatus === "") return unavailable;
  let parsed: unknown = dataStatus;
  if (typeof dataStatus === "string") {
    try {
      parsed = JSON.parse(dataStatus);
    } catch (error) {
      if (!(error instanceof SyntaxError)) throw error;
      return { ...unavailable, error: "Historical usage data could not be read: invalid DataStatus JSON." };
    }
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { ...unavailable, error: "Historical usage data could not be read: invalid DataStatus format." };
  }
  const previous = "PreviousWeekValue" in parsed ? parsed.PreviousWeekValue : null;
  const fourWeeksAgo = "FourWeeksAgoValue" in parsed ? parsed.FourWeeksAgoValue : null;
  const invalid = [previous, fourWeeksAgo].some(
    (value) => value !== null && value !== undefined && !isFiniteNumber(value)
  );
  return {
    previousWeekValue: isFiniteNumber(previous) ? previous : null,
    fourWeeksAgoValue: isFiniteNumber(fourWeeksAgo) ? fourWeeksAgo : null,
    ...(invalid ? { error: "Historical usage data contains an invalid comparison value." } : {})
  };
};

const UsageChange: React.FC<{
  label: string;
  description: string;
  value: number | null | undefined;
  comparisonLabel: string;
  comparisonValue: number | null;
  lowerIsBetter: boolean;
}> = ({ label, description, value, comparisonLabel, comparisonValue, lowerIsBetter }) => {
  const available = isFiniteNumber(value);
  const improved = available && (lowerIsBetter ? value < 0 : value > 0);
  const color = !available || value === 0 ? "#616161" : improved ? "#107C10" : "#C50F1F";
  const formatted = available ? `${changeFormat.format(Math.abs(value))}%` : "Not available";
  const direction = !available || value === 0 ? "" : value > 0 ? "Increased by " : "Decreased by ";
  const outcome = !available || value === 0 ? "" : improved ? "; improvement" : "; decline";

  return (
    <Box role="group" aria-label={`${label} change`} sx={{ minWidth: 0 }}>
      <Typography sx={{ color: "#616161", fontSize: 14, fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ mt: 0.25, color: "#242424", fontSize: 14 }}>
        {comparisonLabel}:{" "}
        <Box component="span" sx={{ fontWeight: 800 }}>
          {isFiniteNumber(comparisonValue) ? valueFormat.format(comparisonValue) : "Not available"}
        </Box>
      </Typography>
      <Box
        role="group"
        aria-label={`${direction}${formatted}${outcome}`}
        sx={{ mt: 0.5, display: "flex", alignItems: "center", color }}
      >
        {available && value > 0 && <ArrowDropUp aria-hidden="true" />}
        {available && value < 0 && <ArrowDropDown aria-hidden="true" />}
        <Typography sx={{ fontSize: available ? 24 : 15, fontWeight: 800 }}>{formatted}</Typography>
      </Box>
      <Typography sx={{ mt: 0.25, color: "#616161", fontSize: 14 }}>{description}</Typography>
    </Box>
  );
};

const UsageMetricsCards: React.FC<UsageMetricsCardsProps> = ({
  data,
  product,
  timeframe,
  isFetching,
  isError,
  onRetry,
  children
}) => {
  const selectedMetrics = metrics
    .map((metric) => ({
      ...metric,
      row: data.find(
        ({ productName, feedbackPeriod, metricLabel }) =>
          Boolean(product) &&
          productName === product &&
          feedbackPeriod === timeframe &&
          metricLabel?.trim().toLowerCase() === metric.label.toLowerCase()
      )
    }))
    .filter(({ row }) => isFiniteNumber(row?.metricValueReal));

  const feedbackCardCount = React.Children.count(children);
  const visibleUsageCardCount = isFetching || isError ? 0 : selectedMetrics.length;
  const wideFeedbackSpan =
    feedbackCardCount === 1 || (feedbackCardCount === 2 && visibleUsageCardCount !== 1) ? "span 2" : "auto";
  if (!isFetching && !isError && selectedMetrics.length === 0 && feedbackCardCount === 0) return null;

  return (
    <Box
      component="section"
      aria-label={feedbackCardCount > 0 ? "Usage and feedback metrics" : "Usage metrics"}
      sx={{
        mt: 2,
        display: "grid",
        gridTemplateColumns:
          feedbackCardCount > 0
            ? { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" }
            : { xs: "minmax(0, 1fr)", md: `repeat(${Math.max(1, selectedMetrics.length)}, minmax(0, 1fr))` },
        gap: 2
      }}
    >
      {isFetching ? (
        <Box role="status" sx={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={18} />
          <Typography sx={{ fontSize: 13 }}>Loading usage metrics...</Typography>
        </Box>
      ) : isError ? (
        <Alert
          severity="error"
          sx={{ gridColumn: "1 / -1" }}
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Unable to load usage metrics. Please try again.
        </Alert>
      ) : (
        <Box sx={{ display: "contents" }}>
          {selectedMetrics.map(({ label, title, icon: MetricIcon, color, lowerIsBetter, row }, index) => {
            const history = parseUsageHistory(row?.dataStatus);
            return (
              <Paper
                key={label}
                component="section"
                aria-label={title}
                elevation={0}
                sx={{
                  position: "relative",
                  minWidth: 0,
                  gridColumn:
                    feedbackCardCount > 0
                      ? { xs: "1 / -1", md: selectedMetrics.length > 1 ? "auto" : "1 / -1", xl: "span 2" }
                      : "auto",
                  overflow: "hidden",
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1.2fr)" },
                  alignItems: "start",
                  gap: 1.5,
                  p: 1.5,
                  pl: 2,
                  border: `1px solid ${color}40`,
                  borderRadius: "10px",
                  bgcolor: "#FFFFFF",
                  boxShadow: "0 2px 5px rgba(15,45,75,.08)",
                  "&::after": {
                    content: "\"\"",
                    position: "absolute",
                    width: 100,
                    height: 100,
                    top: -50,
                    right: -40,
                    borderRadius: "50%",
                    bgcolor: `${color}10`
                  },
                  "&::before": {
                    content: "\"\"",
                    position: "absolute",
                    inset: "0 auto 0 0",
                    width: 5,
                    bgcolor: color
                  }
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "40px minmax(0, 1fr)",
                    alignItems: "center",
                    columnGap: 1.25,
                    rowGap: 0.7,
                    minWidth: 0
                  }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 40,
                      height: 40,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                      borderRadius: "8px",
                      color: "#FFF",
                      bgcolor: color,
                      boxShadow: `0 5px 12px ${color}35`
                    }}
                  >
                    <MetricIcon sx={{ fontSize: 25 }} />
                  </Box>
                  <Typography
                    component="h3"
                    sx={{
                      color: "#242424",
                      fontSize: 12,
                      fontWeight: 800,
                      lineHeight: 1.5,
                      textTransform: "uppercase"
                    }}
                  >
                    {title}
                  </Typography>
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 0.75
                    }}
                  >
                    <Typography sx={{ color: "#091F3A", fontSize: 27, lineHeight: 1.1, fontWeight: 800 }}>
                      {isFiniteNumber(row?.metricValueReal) ? valueFormat.format(row.metricValueReal) : "Not available"}
                    </Typography>
                    {label !== "Active Customers" && (
                      <Chip
                        label={row?.metricUnit?.trim() || "Unit unavailable"}
                        size="small"
                        sx={{ bgcolor: `${color}16`, color, fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}
                      />
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 1,
                    pt: { xs: 1, sm: 0 },
                    pl: { xs: 0, sm: 1.5 },
                    borderTop: { xs: "1px solid #E0E0E0", sm: "none" },
                    borderLeft: { xs: "none", sm: "1px solid #E0E0E0" }
                  }}
                >
                  <UsageChange
                    label="WoW"
                    description="vs. previous week"
                    value={row?.woWChangePct}
                    comparisonLabel="Previous week"
                    comparisonValue={history.previousWeekValue}
                    lowerIsBetter={lowerIsBetter}
                  />
                  <Box sx={{ pl: 1, borderLeft: "1px solid #E0E0E0" }}>
                    <UsageChange
                      label="Wo4W"
                      description="vs. previous 4 weeks"
                      value={row?.wo4WChangePct}
                      comparisonLabel="4 weeks ago"
                      comparisonValue={history.fourWeeksAgoValue}
                      lowerIsBetter={lowerIsBetter}
                    />
                  </Box>
                  {history.error && (
                    <Alert severity="warning" sx={{ gridColumn: "1 / -1" }}>
                      {history.error}
                    </Alert>
                  )}
                </Box>
                {index === 0 && (
                  <Typography
                    component="p"
                    sx={{ gridColumn: "1 / -1", m: 0, fontSize: 12, overflowWrap: "anywhere", zIndex: 1 }}
                  >
                    Refer{" "}
                    <Link
                      href="https://salmon-mud-0fdd7e61e.7.azurestaticapps.net/"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ fontSize: "inherit" }}
                    >
                      Perception &amp; MDASH LT Scorecard
                    </Link>{" "}
                    for deeper telemetry signals.
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      )}
      {feedbackCardCount > 0 && (
        <Box sx={{ display: "contents", "& > .MuiPaper-root": { gridColumn: { xs: "auto", xl: wideFeedbackSpan } } }}>
          {children}
        </Box>
      )}
    </Box>
  );
};

export default UsageMetricsCards;
